import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import { X, ExternalLink } from 'lucide-react';
import { useContractServices } from '../../context/ContractServicesContext';

/**
 * A modal component for investing in Innovation Units
 */
const InvestIUModal = ({ 
  project, 
  isOpen, 
  onClose, 
  getExplorerUrl,
  onSuccess
}) => {
  // Contract services
  const { services, executeContractTransaction, isReady } = useContractServices();

  // Form state
  const [investUnits, setInvestUnits] = useState("");
  const [userCelBalance, setUserCelBalance] = useState(null);
  const [remainingInvestorAllocation, setRemainingInvestorAllocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  
  // Fee and cost breakdown state
  const [buyFeePercentage, setBuyFeePercentage] = useState({ raw: 0, formatted: '0.00' });
  const [costBreakdown, setCostBreakdown] = useState({
    basePayment: ethers.constants.Zero,
    fee: ethers.constants.Zero,
    totalCost: ethers.constants.Zero,
    formattedBase: '0',
    formattedFee: '0',
    formattedTotal: '0'
  });

  // Fetch user's CEL balance, fee percentage, and remaining allocation when modal opens
  useEffect(() => {
    if (isOpen && isReady && project) {
      fetchUserCelBalance();
      fetchRemainingInvestorAllocation();
      fetchBuyFeePercentage();
    }
  }, [isOpen, isReady, project]);

  // Recalculate cost breakdown when invest units changes
  useEffect(() => {
    if (investUnits && Number(investUnits) > 0 && isReady && project) {
      calculateCostBreakdown(investUnits);
    } else {
      setCostBreakdown({
        basePayment: ethers.constants.Zero,
        fee: ethers.constants.Zero,
        totalCost: ethers.constants.Zero,
        formattedBase: '0',
        formattedFee: '0',
        formattedTotal: '0'
      });
    }
  }, [investUnits, isReady, project]);

  // Function to fetch buy fee percentage
  const fetchBuyFeePercentage = async () => {
    if (!isReady || !services?.innovationUnits) return;
    
    try {
      const feePercentage = await services.innovationUnits.getBuyFeePercentage();
      setBuyFeePercentage(feePercentage);
    } catch (error) {
      console.error("Error fetching buy fee percentage:", error);
    }
  };

  // Function to fetch user's CEL token balance
  const fetchUserCelBalance = async () => {
    if (!isReady || !services?.celToken) return;
    
    try {
      setLoadingBalance(true);
      // Get signer's address
      const address = await services.celToken.contract.signer.getAddress();
      
      // Get CEL balance
      const balance = await services.celToken.balanceOf(address);
      setUserCelBalance(ethers.utils.formatEther(balance));
    } catch (error) {
      console.error("Error fetching CEL balance:", error);
    } finally {
      setLoadingBalance(false);
    }
  };

  // Function to fetch remaining investor allocation
  const fetchRemainingInvestorAllocation = async () => {
    if (!isReady || !services?.innovationUnits) return;
    
    try {
      setLoadingAllocation(true);
      
      // Get the project ID
      const projectId = getProjectId();
      
      if (isNaN(projectId) || projectId <= 0) {
        console.error("Invalid project ID");
        return;
      }
      
      console.log(`Fetching investor allocation for project ${projectId}`);
      const remaining = await services.innovationUnits.getRemainingInvestorAllocation(projectId);
      console.log(`Raw remaining allocation from contract: ${remaining.toString()}`);
      
      // Use formatUnits with 0 decimals for Innovation Units
      setRemainingInvestorAllocation(ethers.utils.formatUnits(remaining, 0));
    } catch (error) {
      console.error("Error fetching remaining investor allocation:", error);
    } finally {
      setLoadingAllocation(false);
    }
  };

  // Calculate cost breakdown using the contract function
  const calculateCostBreakdown = async (units) => {
    if (!isReady || !services?.innovationUnits || !units || Number(units) <= 0) return;
    
    try {
      const projectId = getProjectId();
      // Convert to BigNumber with no decimals for IU amounts
      const amount = ethers.BigNumber.from(Math.floor(Number(units)));
      
      // Call the contract to get the exact calculation
      const { basePayment, fee, totalCost } = await services.innovationUnits.calculateBuyingCost(
        projectId,
        amount
      );
      
      setCostBreakdown({
        basePayment,
        fee,
        totalCost,
        formattedBase: ethers.utils.formatEther(basePayment),
        formattedFee: ethers.utils.formatEther(fee),
        formattedTotal: ethers.utils.formatEther(totalCost)
      });
    } catch (error) {
      console.error("Error calculating cost breakdown:", error);
    }
  };

  // Get the projectId from the project object
  const getProjectId = () => {
    if (project?.transactionData?.projectId) {
      return parseInt(project.transactionData.projectId);
    } else if (project?.projectId) {
      return parseInt(project.projectId);
    } else if (project?.projectTokenId) {
      return parseInt(project.projectTokenId);
    }
    
    throw new Error("Invalid project ID. Please ensure the project has a valid blockchain ID.");
  };

  // Calculate ownership percentage
  const calculateOwnershipPercentage = (units) => {
    if (!project || !project.totalInnovationUnits || units === "") return "0";
    return (
      (Number(units) / Number(project.totalInnovationUnits)) *
      100
    ).toFixed(2);
  };

  // Handle investment units input change
  const handleInvestUnitsChange = (e) => {
    const value = e.target.value;
    setInvestUnits(value);
  };

  // Set max investment amount based on remaining allocation or CEL balance
  const setMaxAmount = async () => {
    if (!isReady || !services?.innovationUnits || !project) return;
    
    try {
      // Get remaining allocation
      if (!remainingInvestorAllocation) {
        await fetchRemainingInvestorAllocation();
      }
      
      // Get max units based on CEL balance (how many IUs can the user buy with their CEL balance)
      const projectId = getProjectId();
      let maxUnits = parseInt(remainingInvestorAllocation);
      
      // Start with 90% of max units (to account for rounding and gas)
      let testUnits = Math.floor(maxUnits * 0.9);
      
      // Only calculate max based on CEL balance if user has balance
      if (userCelBalance && Number(userCelBalance) > 0) {
        let found = false;
        let iterations = 0;
        const maxIterations = 5; // Limit iterations to prevent infinite loop
        
        while (!found && iterations < maxIterations && testUnits > 0) {
          iterations++;
          
          try {
            // Calculate cost for test units
            const { totalCost } = await services.innovationUnits.calculateBuyingCost(
              projectId,
              ethers.BigNumber.from(testUnits)
            );
            
            // Check if user has enough balance
            const requiredCEL = ethers.utils.formatEther(totalCost);
            if (Number(requiredCEL) <= Number(userCelBalance)) {
              found = true;
              maxUnits = testUnits;
            } else {
              // Reduce test units by 10%
              testUnits = Math.floor(testUnits * 0.9);
            }
          } catch (error) {
            console.error("Error in max calculation:", error);
            testUnits = Math.floor(testUnits * 0.8); // Reduce more aggressively on error
          }
        }
      }
      
      // Set max units
      setInvestUnits(maxUnits.toString());
    } catch (error) {
      console.error("Error setting max amount:", error);
    }
  };

  // Submit investment transaction
  const submitInvestment = async () => {
    if (!isReady) {
      toast.error("Smart contract services are not ready. Please connect your wallet and try again.");
      return;
    }
    
    if (!investUnits || parseFloat(investUnits) <= 0) {
      toast.error("Please enter a valid amount of Innovation Units to purchase");
      return;
    }
    
    try {
      setLoading(true);
      
      // Get the project ID
      const projectId = getProjectId();
      
      console.log(`Investing in project ${projectId}, buying ${investUnits} IUs`);
      
      // Show a toast indicating the process may take two steps
      toast.info(
        <div>
          <p className="font-bold">Investment Process Started</p>
          <p className="text-sm mt-1">This is a two-step process:</p>
          <ol className="text-sm mt-1 list-decimal list-inside">
            <li>Token approval (if needed)</li>
            <li>Purchase transaction</li>
          </ol>
          <p className="text-xs mt-2 text-blue-600">Please confirm each transaction in your wallet when prompted.</p>
        </div>,
        { autoClose: 10000, position: "top-center" }
      );
      
      // Convert to wei amounts for IUs (whole numbers only)
      const iuAmount = ethers.BigNumber.from(Math.floor(parseFloat(investUnits)));
      
      // Check available investor allocation first
      const remainingAllocation = await services.innovationUnits.getRemainingInvestorAllocation(projectId);
      console.log(`Remaining investor allocation: ${ethers.utils.formatUnits(remainingAllocation, 0)} IUs`);
      
      if (iuAmount.gt(remainingAllocation)) {
        throw new Error(`Cannot buy ${investUnits} IUs. Only ${ethers.utils.formatUnits(remainingAllocation, 0)} IUs available for investment.`);
      }
      
      // Calculate cost first to show user
      const { totalCost } = await services.innovationUnits.calculateBuyingCost(projectId, iuAmount);
      console.log(`Total cost to buy ${investUnits} IUs: ${ethers.utils.formatEther(totalCost)} CEL`);
      
      // Check user balance
     const signer = services.celToken.contract.signer;
      if (!signer) {
        throw new Error("Wallet signer not available. Please reconnect your wallet.");
      }

      const walletAddress = await signer.getAddress();

      
      const userBalance = await services.celToken.balanceOf(walletAddress);
      
      if (userBalance.lt(totalCost)) {
        throw new Error(`Insufficient CEL balance. You have ${ethers.utils.formatEther(userBalance)} CEL but need ${ethers.utils.formatEther(totalCost)} CEL.`);
      }
      
      // Execute the buy transaction
      const result = await executeContractTransaction(
        services.innovationUnits.buyIUs.bind(services.innovationUnits),
        [projectId, iuAmount],
        {
          pendingMessage: 'Processing investment transaction...',
          successMessage: 'Investment transaction submitted successfully!',
          failMessage: 'Failed to complete investment',
          showToast: false // Don't show default toast, we'll handle it
        }
      );
      
      // Log the full transaction result for debugging
      console.log("Full transaction result:", result);
      
      // If transaction was successful, update the DB and refresh project data
      // Handle results with either hash or transactionHash property
      const txHash = result?.transactionHash || result?.hash;
      
      if (txHash) {
        console.log(`Transaction successful with hash: ${txHash}`);
        
        // Toast success with details about the transaction
        toast.success(
          <div>
            <p className="font-bold">Investment Successful!</p>
            <p className="text-sm mt-1">You have purchased {investUnits} IUs for {ethers.utils.formatEther(totalCost)} CEL</p>
            <div className="text-xs mt-2">
              <a 
                href={getExplorerUrl(txHash)}
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                View transaction
              </a>
            </div>
          </div>,
          { autoClose: 8000, position: "top-center" }
        );
        
        // Close modal and reset form
        onClose();
        setInvestUnits("");
        
        // Call success callback to update DB and refresh data
        if (onSuccess) {
          // Create investment data object
          const investmentData = {
            investUnits: investUnits,
            totalCost: totalCost
          };
          
          onSuccess(txHash, investmentData);
        }
      } else {
        console.error("Transaction result missing hash:", result);
        toast.error("Investment transaction may have failed. Please check your wallet and refresh the page.");
      }
    } catch (error) {
      console.error("Investment error:", error);
      
      // Check for specific error types for better user feedback
      let errorMessage = error.message || 'Failed to complete investment';
      
      if (errorMessage.includes("user rejected") || errorMessage.includes("User denied")) {
        toast.error("Transaction was rejected. Please try again and approve the transaction in your wallet.");
      } else if (errorMessage.includes("insufficient funds") || errorMessage.includes("Insufficient")) {
        toast.error("You don't have enough CEL tokens to complete this transaction.");
      } else if (errorMessage.includes("approve") || errorMessage.includes("allowance")) {
        toast.error("Token approval failed. Please try again and approve the transaction in your wallet.");
      } else if (errorMessage.includes("Exceeds investors allocation")) {
        toast.error("This purchase exceeds the available Innovation Units for investors. Please try a smaller amount.");
      } else {
        toast.error(`Investment failed: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Add a refresh button for allocation
  const refreshAllocation = () => {
    fetchRemainingInvestorAllocation();
    toast.info("Refreshing available allocation...");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Invest in Project</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <div className="mb-6">
          <div className="p-3 bg-blue-50 rounded text-sm mb-4">
            <h4 className="font-semibold mb-1">Investment Information</h4>
            
            {userCelBalance !== null && (
              <p className="mt-1">Your CEL balance: {parseFloat(userCelBalance).toFixed(4)} CEL</p>
            )}
            {remainingInvestorAllocation !== null ? (
              <div className="mt-1">
                <div className="flex justify-between items-center">
                  <p className="text-indigo-700 font-medium">
                    Remaining Investor Allocation: {parseInt(remainingInvestorAllocation)} IUs
                  </p>
                  <button
                    onClick={refreshAllocation}
                    className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded ml-2"
                    title="Refresh allocation data"
                  >
                    Refresh
                  </button>
                </div>
                {parseInt(remainingInvestorAllocation) === 0 && (
                  <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                    <p className="font-medium">No allocation available</p>
                    <p>There are currently no Innovation Units available for investors in this project.</p>
                  </div>
                )}
              </div>
            ) : loadingAllocation ? (
              <p className="mt-1 text-gray-500">Loading available allocation...</p>
            ) : null}
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Number of Innovation Units to buy
            </label>
            <div className="relative">
              <input
                type="number"
                value={investUnits}
                onChange={handleInvestUnitsChange}
                placeholder="Enter amount of IUs"
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
              <button
                type="button"
                onClick={setMaxAmount}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-medium px-2 py-1 rounded"
              >
                Max
              </button>
            </div>
            {investUnits && (
              <p className="text-sm text-gray-500 mt-1">
                This represents {calculateOwnershipPercentage(investUnits)}%
                ownership
              </p>
            )}
            {investUnits && remainingInvestorAllocation !== null && 
              parseInt(investUnits) > parseInt(remainingInvestorAllocation) && (
              <p className="text-xs text-red-500 mt-1">
                Amount exceeds available IUs ({parseInt(remainingInvestorAllocation)} IUs available)
              </p>
            )}
          </div>

          {/* Cost Breakdown */}
          {investUnits && Number(investUnits) > 0 && (
            <div className="mb-6 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="font-semibold mb-2 text-sm">Investment Breakdown</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Investment Amount:</span>
                  <span>{parseFloat(costBreakdown.formattedBase).toFixed(4)} CEL</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Fee ({buyFeePercentage.formatted}%):</span>
                  <span>{parseFloat(costBreakdown.formattedFee).toFixed(4)} CEL</span>
                </div>
                <div className="border-t pt-2 mt-1">
                  <div className="flex justify-between text-sm font-medium">
                    <span>Total Investment Cost:</span>
                    <span>{parseFloat(costBreakdown.formattedTotal).toFixed(4)} CEL</span>
                  </div>
                </div>
              </div>
              {userCelBalance && parseFloat(costBreakdown.formattedTotal) > parseFloat(userCelBalance) && (
                <p className="text-xs text-red-500 mt-2">
                  Total cost exceeds your balance of {parseFloat(userCelBalance).toFixed(4)} CEL
                </p>
              )}
            </div>
          )}

          {/* Investment Preview */}
          {investUnits && parseFloat(investUnits) > 0 && (
            <div className="mb-6 p-3 bg-gray-50 rounded">
              <h4 className="font-semibold mb-2 text-sm">Investment Preview</h4>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Units to purchase:</span>
                <span className="font-medium">{parseFloat(investUnits).toFixed(2)} IUs</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Total cost:</span>
                <span className="font-medium">{parseFloat(costBreakdown.formattedTotal).toFixed(4)} CEL</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Ownership:</span>
                <span className="font-medium">{calculateOwnershipPercentage(investUnits)}%</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={submitInvestment}
            disabled={
              loading ||
              !investUnits || 
              Number(investUnits) <= 0 || 
              (userCelBalance && parseFloat(costBreakdown.formattedTotal) > parseFloat(userCelBalance)) ||
              (remainingInvestorAllocation !== null && parseInt(investUnits) > parseInt(remainingInvestorAllocation))
            }
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : 'Confirm Investment'}
          </button>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestIUModal; 